import {
	createFileRoute,
	Link,
	stripSearchParams,
	useParams,
	useRouteContext,
} from "@tanstack/react-router";
import { zodValidator } from "@tanstack/zod-adapter";
import { addDays } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { graphql, useFragment, useLazyLoadQuery } from "react-relay";
import { z } from "zod";
import { Icon } from "@/components/icon";
import { Button } from "@/components/ui/button";
import { Image } from "@/components/image";
import { Drawer, DrawerContent, DrawerTrigger } from "@/components/ui/drawer";
import { CourseLesson } from "@/ui/course-lesson";
import { CourseQuiz } from "@/ui/course-quiz";
import {
	CourseSectionsPreview,
	CourseSectionsPreviewContent,
} from "@/ui/course-sections-preview";
import { LessonBlocked } from "@/ui/lesson-blocked";
import { TopNavRightPanel } from "@/ui/topnav";
import { cn } from "@/utils/cn";
import type { cursos_lesson$key } from "./__generated__/cursos_lesson.graphql";
import type { cursosLessonQuery } from "./__generated__/cursosLessonQuery.graphql";
import { NovuProvider } from "@/utils/novu-provider";

const defaultValues = {
	comment_order: "trending",
	autoPlay: false,
} as const;

export const Route = createFileRoute(
	"/_visibility/_standalone/cursos/$course/$lesson",
)({
	component: RouteComponent,
	validateSearch: zodValidator(
		z.object({
			comment_order: z
				.enum(["trending", "newest", "oldest"])
				.default("trending"),
			autoPlay: z.boolean().default(false),
		}),
	),
	search: {
		middlewares: [stripSearchParams(defaultValues)],
	},
});

function RouteComponent() {
	const [drawerOpen, setDrawerOpen] = useState(false);
	const { club, session } = useRouteContext({ from: "__root__" });
	const { course, lesson } = useParams({ from: Route.id });
	const queryData = useLazyLoadQuery<cursosLessonQuery>(
		graphql`
    query cursosLessonQuery($course: String!, $slug: String!, $club: String!, $username: String!) {
      courseClass(course: $course, slug: $slug, club: $club) {
        ...cursos_lesson @arguments(username: $username)
        ...courseLessonComments_lesson
      }

      user {
        ...topnavRightPanel_user
      }

			club(slug: $club) {
				aiPersona {
					enabled
				}
				showPoweredLogo
			}
    }
  `,
		{
			course,
			slug: lesson,
			club: club.slug,
			username: session?.user?.username!,
		},
	);

	const data = useFragment<cursos_lesson$key>(
		graphql`
	    fragment cursos_lesson on CourseClass
			@argumentDefinitions(username: { type: "String!" })
			{
				id

        name
        canAccess

        section {
          id
        }

        previousClass {
          completed
        }

        course {
          name
          progress
          slug
          classAccessControl

          member(user: $username) {
            id
            enrolledAt
            memberGroup {
                group {
                  releaseTimeline {
                    section {
                      id
                    }
                    daysToRelease
                    notAvailableBefore
                  }
                }
            }
          }

          ...courseSectionsPreview
        }

        ... on CourseContent {
          ...courseLesson_lesson
        }

        ... on CourseQuiz {
          ...courseQuiz_quiz
        }

        ...lessonBlocked_lesson

        __typename
      }
	`,
		queryData.courseClass,
	);

	const isAdmin = club.permissions.length > 0;

	const availableDate = useMemo(() => {
		if (data.canAccess) {
			return undefined;
		}

		const sectionReleaseTimeline =
			data.course.member?.memberGroup.group.releaseTimeline?.find(
				(s) => s.section.id === data.section.id,
			);

		if (sectionReleaseTimeline?.daysToRelease) {
			return addDays(
				new Date(data.course.member?.enrolledAt),
				sectionReleaseTimeline?.daysToRelease,
			);
		}

		if (sectionReleaseTimeline?.notAvailableBefore) {
			return new Date(sectionReleaseTimeline?.notAvailableBefore);
		}

		return undefined;
	}, [
		data.course.member?.memberGroup.group.releaseTimeline,
		data.canAccess,
		data.course.member?.enrolledAt,
		data.section.id,
	]);

	const mustCompletePreviousLesson = Boolean(
		data.course.classAccessControl === "PROGRESSIVE" &&
			!data.previousClass?.completed,
	);

	useEffect(() => {
		if (lesson) {
			setDrawerOpen(false);
		}
	}, [lesson]);

	return (
		<div
			className={cn(
				"w-full h-full md:flex md:flex-col md:overflow-y-hidden md:flex-1 lg:min-h-dvh lg:max-h-dvh",
				isAdmin && "lg:min-h-[calc(100dvh-2rem)] lg:max-h-[calc(100dvh-2rem)]",
			)}
		>
			<div
				className={cn(
					"grid max-md:h-[72px] min-h-[60px] md:h-[60px] border-b z-3 w-full grid-cols-3 max-lg:grid-cols-2 px-6 max-lg:px-2 bg-sidebar backdrop-blur-md text-sidebar-foreground",
				)}
			>
				<div className="flex flex-row items-center justify-start gap-3">
					<Link
						type="button"
						className="flex flex-row items-center justify-start gap-2"
						to="/cursos"
					>
						<Button variant="ghost">
							<Icon name="MoveLeft" />
							Cursos
						</Button>
					</Link>

					{Boolean(queryData.club.showPoweredLogo) && (
						<Link
							to="https://scarf.club"
							target="_blank"
							className="hidden rounded-md border bg-muted px-3 py-1.5 text-muted-foreground text-xs lg:block"
						>
							<span className="flex items-center text-muted-foreground leading-none">
								Powered by{" "}
								<Image
									src={
										"https://storage.googleapis.com/scarf-club-assets/logos/Scarf-Full-Primary.svg"
									}
									className="ml-1.5 inline h-4"
								/>
							</span>
						</Link>
					)}
				</div>

				<div className="flex items-center flex-col justify-center max-lg:hidden">
					<div className="font-semibold text-foreground">
						{data.course.name}
					</div>

					<div className="flex flex-row items-center justify-center gap-3">
						<div className="w-[150px] flex flex-row bg-muted h-1 rounded-full overflow-hidden">
							<div
								className="h-full bg-primary"
								style={{ flex: data.course.progress / 10000 }}
							/>
							<div style={{ flex: 1 - data.course.progress / 10000 }} />
						</div>

						<span className="text-xs/5">
							{(data.course.progress / 10000).toLocaleString("pt-BR", {
								style: "percent",
								minimumFractionDigits: 0,
								maximumFractionDigits: 1,
							})}
						</span>
					</div>
				</div>

				<div className="flex items-center justify-end max-lg:hidden">
					<NovuProvider>
						<TopNavRightPanel
							hideAi={!queryData.club.aiPersona?.enabled}
							hideSearch
							user={queryData.user || undefined}
						/>
					</NovuProvider>
				</div>

				<div className="lg:hidden flex items-center justify-end h-full">
					<Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
						<DrawerTrigger asChild>
							<Button variant="ghost">
								<Icon name="Menu" className="size-4" />
								Módulos
							</Button>
						</DrawerTrigger>

						<DrawerContent>
							<div className="size-full mt-6 overflow-y-auto">
								<CourseSectionsPreviewContent
									course={data.course}
									currentSection={data.section.id}
									currentLessonId={data.id}
								/>
							</div>
						</DrawerContent>
					</Drawer>
				</div>
			</div>

			<div className="p-6 flex-1 flex flex-row gap-6 max-h-full md:overflow-y-auto items-start justify-center w-full max-md:p-0 max-md:py-3 overflow-x-hidden">
				<div className="max-w-[1100px] flex flex-row gap-6 w-full">
					{!data.canAccess && (
						<LessonBlocked
							lesson={data}
							availableAt={availableDate}
							requiresPreviousLesson={mustCompletePreviousLesson}
						/>
					)}

					{data.canAccess && data.__typename === "CourseContent" ? (
						<CourseLesson lesson={data} />
					) : null}

					{data.canAccess && data.__typename === "CourseQuiz" ? (
						<CourseQuiz quiz={data} />
					) : null}

					<div className="w-full max-w-[340px] max-lg:hidden">
						<CourseSectionsPreview
							course={data.course}
							currentSection={data.section.id}
							currentLessonId={data.id}
						/>
					</div>
				</div>
			</div>
		</div>
	);
}
